import AircraftCard from "../components/quote/AircraftCard";
import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router";
import useClickOutside from "../hooks/useClickOutside";
import QuoteHeader from "../components/quote/QuoteHeader";
import QuoteFilters from "../components/quote/QuoteFilters";
import QuoteCtaBar from "../components/quote/QuoteCtaBar";
import { CSSTransition } from "react-transition-group";
import useGet from "../hooks/useGet";
import LoadingOverlay from "../components/shared/LoadingOverlay";
import { useNavigate } from "react-router-dom";
import ErrorModal from "../components/shared/ErrorModal";
import usePost from "../hooks/usePost";
import SEO from "../components/shared/SEO";
import CompareButtonsBar from "../components/quote/CompareButtonsBar";
import Compare from "../components/quote/Compare";
import Button from "../components/ui/Button";
import CloseCross from "../components/ui/CloseCross";
import ReturnMessage from "../components/shared/ReturnMessage";
import { isMockApiEnabled, MOCK_LEG_ID } from "../mockApi";

const Quote = ({ sharedPage, showQuoteOrCompare }) => {
    const navigate = useNavigate();
    const { postData, sending, returnMessage } = usePost();

    const [aircraftData, setAircraftData] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showCompareBar, setShowCompareBar] = useState(false);
    const [compareSelection, setCompareSelection] = useState([]);
    const [filterScrollPosition, setFilterScrollPosition] = useState("");
    const [expanded, setExpanded] = useState(() => new Set());
    const [countFilters, setCountFilters] = useState(0);
    const [, setDimAircraft] = useState(null);
    const [exitAircraft, setExitAircraft] = useState(false);
    const [enterAircraft, setEnterAircraft] = useState(false);
    const [, setCurrentRow] = useState(false);
    const [loading, setLoading] = useState(true);
    const [getSearchResultsApi, setGetSearchResultsApi] = useState(false);
    const [searchResultsHeaderApi, setSearchResultsHeaderApi] = useState(false);
    const [sharedDataApi, setSharedDataApi] = useState(false);
    const [error, setError] = useState(false);
    const [thumbs, setThumbs] = useState([]);
    const [sharedMessage, setSharedMessage] = useState(null);

    const extraContent = useRef();
    const confirmApproved = useRef();

    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const approvalRequested = queryParams.get("approvalRequested")?.toLowerCase();
    const approverId = queryParams.get("approverId");
    const sharerName = queryParams.get("sharerName");
    const name = queryParams.get("name");
    const rfqId = queryParams.get("qi");

    const [legId, setLegId] = useState(queryParams.get("lo") || (isMockApiEnabled() ? MOCK_LEG_ID : null));

    const backLink = search;

    const {
        loading: searchResultsLoading,
        error: searchError,
        returnData: searchResults,
    } = useGet(getSearchResultsApi);
    const {
        loading: searchResultsHeaderLoading,
        error: headerError,
        returnData: searchResultsHeader,
    } = useGet(searchResultsHeaderApi);
    const { returnData: sharedData } = useGet(sharedDataApi);

    useEffect(() => {
        if (!legId) {
            if (sharedPage) {
                setSharedDataApi(
                    `/api/frontend/get-shared-flights-from-journeyid?journeyId=${rfqId}&approverId=${approverId}`
                );
            } else {
                navigate("/");
            }
        } else {
            setGetSearchResultsApi(`/api/frontend/v2/get-search-results?LegId=${legId}`);
            setSearchResultsHeaderApi(`/api/frontend/get-search-results-header?LegId=${legId}`);
        }

        if (returnMessage.success && returnMessage.postRef === "postApproval") {
            navigate("/approve-flights-success");
        }

        if (searchResultsHeader.expired || sharedData.sharedQuoteIsExpired) {
            setError("The page you have clicked to view has now expired or you have already completed this step.");

            setLoading(false);
        }

        if (!searchResultsLoading && !searchResultsHeaderLoading && searchResults && searchResultsHeader) {
            if (searchResults[0]) {
                if (!aircraftData) {
                    resetAircraftData(searchResults);
                }
            } else {
                setError(
                    "Sorry, no quote was found. It may be that your broker hasn't put it together yet, please check back in a few hours. Alternatively you may be signed in with a different account."
                );
            }

            setLoading(false);
        }

        if (searchError || headerError) {
            setError(headerError ? headerError : searchError);
            setLoading(false);
        }

        if (sharedPage && sharerName && aircraftData && aircraftData.length !== 0) {
            let message;
            if (aircraftData.length === 1) {
                message = `${sharerName} has shared this flight with you`;
            }
            if (aircraftData.length > 1) {
                message = `${sharerName} has shared these flights with you`;
            }
            if (approvalRequested === "true") {
                message = `${message} and requests your approval`;
            }
            setSharedMessage(message);
        }

        let resizeTimeout;

        const resizeHandler = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {}, 100);
        };

        resizeHandler();
        window.addEventListener("resize", resizeHandler);

        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
        // approverId/rfqId/sharedData.sharedQuoteIsExpired/resetAircraftData
        // are intentionally omitted — resetAircraftData is a local closure,
        // and the others are stable per route. The real triggers are listed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        aircraftData,
        approvalRequested,
        headerError,
        legId,
        navigate,
        returnMessage,
        searchError,
        searchResults,
        searchResultsHeader,
        searchResultsHeaderLoading,
        searchResultsLoading,
        sharedPage,
        sharerName,
    ]);

    const resetAircraftData = (data) => {
        if (sharedData) {
            if (sharedData.sharedFlights) {
                if (sharedData.sharedFlights.length > 0) {
                    let tailNumbers = [];

                    sharedData.sharedFlights.forEach((flight) => {
                        tailNumbers.push(flight.tailNumber);
                    });

                    data = data.filter((item) => {
                        return tailNumbers.indexOf(item.tailNumber) > -1;
                    });
                }
            }
        }

        setAircraftData(data.filter((aircraft) => aircraft.show !== false));
    };

    useEffect(() => {
        if (sharedData) {
            if (sharedData.sharedFlights) {
                if (sharedData.sharedFlights.length > 0) {
                    setLegId(sharedData.sharedFlights[0].legId);
                }
            }
        }
    }, [sharedData]);

    const showFiltersHandler = () => {
        setShowFilters(!showFilters);
        setShowCompareBar(false);
    };

    const showCompareBarHandler = () => {
        setShowCompareBar(!showCompareBar);
    };

    const expandedHandler = (aircraftId, index) => {
        if (!aircraftId) {
            setExpanded(new Set());
            setCurrentRow(false);
            setDimAircraft(false);
            return;
        }
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(aircraftId)) {
                next.delete(aircraftId);
            } else {
                next.add(aircraftId);
                setExtraContentPosition(aircraftId, index);
            }
            return next;
        });
    };

    const thumbsHandler = (status, tailNumber) => {
        let updatedSelection = thumbs;
        if (!thumbs.includes(tailNumber)) {
            setThumbs([...updatedSelection, tailNumber]);
        } else {
            setThumbs(updatedSelection.filter((selected) => selected !== tailNumber));
        }
    };

    const aircraftFilterSpeed = 500; // match variable in style/settings/_animation-speeds
    const filterOptionsHandler = (filterOptions) => {
        expandedHandler(false);

        const filterCount = Object.values(filterOptions).filter((item) => item === true).length;
        setCountFilters(filterCount);

        animateEnterExitAircraft("exit", aircraftFilterSpeed, () => {
            if (!window.matchMedia("(min-width: 550px)").matches) {
                window.scrollTo({ top: filterScrollPosition, behavior: "smooth" });
            }

            if (filterCount === 0) {
                resetAircraftData(searchResults);
                animateEnterExitAircraft("enter", aircraftFilterSpeed);
            } else {
                let updatedAircraft = [];

                Object.keys(filterOptions).forEach(function (key) {
                    if (filterOptions[key]) {
                        let filterAircraft = searchResults;

                        if (key === "toilet") {
                            filterAircraft = filterAircraft.filter(
                                (aircraft) => aircraft[key] === "Yes" || aircraft[key] === "Yes Fully Enclosed"
                            );
                        } else {
                            if (key === "wifi") {
                                filterAircraft = filterAircraft.filter(
                                    (aircraft) =>
                                        aircraft[key] === "WiFi Available (Additional cost)" ||
                                        aircraft[key] === "WiFi Included"
                                );
                            } else {
                                filterAircraft = filterAircraft.filter((aircraft) => aircraft[key] === true);
                            }
                        }

                        updatedAircraft = updatedAircraft.concat(filterAircraft);
                    }
                });

                const uniq = {};
                const aircraftNoDuplicates = updatedAircraft.filter((obj) => !uniq[obj.id] && (uniq[obj.id] = true));

                resetAircraftData(aircraftNoDuplicates);
                animateEnterExitAircraft("enter", aircraftFilterSpeed);
            }
        });
    };

    const animateEnterExitAircraft = (direction, timing, callback) => {
        if (direction === "exit") {
            setExitAircraft(true);
            setTimeout(() => {
                callback();
            }, timing);
        }

        if (direction === "enter") {
            setExitAircraft(false);
            setEnterAircraft(true);
            setTimeout(() => {
                setEnterAircraft(false);
            }, timing);
        }
    };

    const filtersButtonRef = useRef(null);
    const filterBarRef = useRef(null);
    const compareButtonRef = useRef(null);
    const compareBarRef = useRef(null);
    useClickOutside([filtersButtonRef, filterBarRef], () => {
        if (showFilters) {
            setShowFilters(false);
        }
    });
    useClickOutside([compareButtonRef, compareBarRef], () => {
        if (showCompareBar) {
            setShowCompareBar(false);
        }
    });

    const cardsWrapperRef = useRef(null);
    useClickOutside([extraContent, cardsWrapperRef], () => {
        if (expanded.size > 0) {
            setExpanded(new Set());
            setCurrentRow(false);
            setDimAircraft(false);
        }
    });

    const compareSelectionHandler = (value, id) => {
        let updatedSelection = compareSelection;
        if (value === true) {
            setCompareSelection([...updatedSelection, id]);
        } else {
            setCompareSelection(updatedSelection.filter((selected) => selected !== id));
        }
    };

    const selectAllHandler = () => {
        let allFlights = [];
        aircraftData.map((flight) => {
            allFlights.push(`${flight.id}___${flight.tailNumber}`);
            return false;
        });

        setCompareSelection(allFlights);
    };

    const compareClearAllHandler = () => {
        setCompareSelection([]);
    };

    const toggleCompareOptionHandler = (id) => {
        setCompareSelection((prevSelection) => {
            if (prevSelection.includes(id)) {
                return prevSelection.filter((selected) => selected !== id);
            }

            return [...prevSelection, id];
        });
    };

    const thumbsClearAllHandler = () => {
        setThumbs([]);
    };

    const sendApprovedFlightsHandler = () => {
        window.scrollTo({ top: 0, behavior: "instant" });

        const data = {
            approverId: approverId,
            approvedFlights: thumbs,
        };

        postData(data, "/api/frontend/approve-shared-flights", 0, "postApproval");
    };

    const setExtraContentPosition = (airCraftId, index) => {
        let row = index;

        let perRow = 1;

        if (window.matchMedia("(min-width: 765px)").matches) {
            perRow = 2;
            row = index;

            const diff = (index + 1) % perRow;

            if (diff) {
                row += perRow - diff;
            }
        }

        if (window.matchMedia("(min-width: 1024px)").matches) {
            perRow = 3;
            row = index;

            const diff = (index + 1) % perRow;

            if (diff) {
                row += perRow - diff;
            }
        }

        if (row > aircraftData.length - 1) {
            row = aircraftData.length - 1;
        }

        setCurrentRow(row);
    };

    return (
        <>
            <SEO title="Quote" />
            <div className="quote">
                {loading || sending ? (
                    <LoadingOverlay clear={true} />
                ) : error ? (
                    <ErrorModal errorMessage={error} />
                ) : (
                    <>
                        {returnMessage.message && <ReturnMessage inline returnMessage={returnMessage} />}

                        {sharedMessage && !returnMessage.error && (
                            <div id="header-message" className="quote__header-message">
                                {sharedMessage}
                            </div>
                        )}

                        <QuoteHeader
                            data={searchResultsHeader}
                            filterScrollPosition={(position) => setFilterScrollPosition(position)}
                            backgroundColor="transparent"
                            ownerApproval={aircraftData?.[0]?.ownerApproval}
                        />

                        {showQuoteOrCompare === "quote" ? (
                            <>
                                <QuoteCtaBar
                                    filtersButtonRef={filtersButtonRef}
                                    compareButtonRef={compareButtonRef}
                                    showFilters={showFilters}
                                    showCompareBar={showCompareBar}
                                    showFiltersHandler={showFiltersHandler}
                                    showCompareBarHandler={showCompareBarHandler}
                                    filterCount={countFilters}
                                    compareSelection={compareSelection}
                                    backLink={backLink}
                                    legId={legId}
                                    name={name}
                                />

                                {aircraftData && (
                                    <div className="quote__results-count wrap wrap--sm-no-padding">
                                        {aircraftData.length} quote{aircraftData.length === 1 ? "" : "s"} available
                                    </div>
                                )}

                                <div className="quote__filter-and-compare-content">
                                    <QuoteFilters
                                        showFilters={showFilters}
                                        filterBarRef={filterBarRef}
                                        filtersButtonRef={filtersButtonRef}
                                        selectedFilter={(filterOptions) => filterOptionsHandler(filterOptions)}
                                    />

                                    <CompareButtonsBar
                                        showCompare={showCompareBar}
                                        compareBarRef={compareBarRef}
                                        compareButtonRef={compareButtonRef}
                                        numberOfOptions={aircraftData.length}
                                        compareOptions={aircraftData.map((aircraft) => ({
                                            id: `${aircraft.id}___${aircraft.tailNumber}`,
                                            name: aircraft.aircraftName,
                                        }))}
                                        showCompareBarHandler={showCompareBarHandler}
                                        compareSelection={compareSelection}
                                        onToggleOption={toggleCompareOptionHandler}
                                        selectAllHandler={selectAllHandler}
                                        clearAllHandler={compareClearAllHandler}
                                        closeCompare={() => setShowCompareBar(false)}
                                        sharedPage={sharedPage}
                                        backLink={backLink}
                                    />

                                    <CSSTransition
                                        nodeRef={confirmApproved}
                                        in={thumbs.length > 0}
                                        timeout={500}
                                        unmountOnExit
                                        classNames={{
                                            enterDone: "quote__confirm-approve--active",
                                            enterActive: "quote__confirm-approve--transition-in",
                                            exitActive: "quote__confirm-approve--transition-out",
                                        }}
                                    >
                                        <div ref={confirmApproved} className="quote__confirm-approve">
                                            <div className="quote__confirm-approve__inner">
                                                <div className="quote__confirm-approve__inner__title">
                                                    {thumbs.length} flight{thumbs.length === 1 ? null : "s"} selected
                                                </div>
                                                <div className="quote__confirm-approve__inner__buttons-wrapper">
                                                    <Button onClick={sendApprovedFlightsHandler}>
                                                        Send approval to {sharerName}
                                                    </Button>
                                                </div>
                                                <div className="quote__confirm-approve__inner__close-cross">
                                                    <CloseCross onClick={thumbsClearAllHandler} />
                                                </div>
                                            </div>
                                        </div>
                                    </CSSTransition>
                                </div>

                                <div className="wrap wrap--sm-no-padding">
                                    <div ref={cardsWrapperRef} className="quote__aircraft-grid">
                                        {aircraftData.length === 0 && (
                                            <p className="quote__no-results">No results matching your search</p>
                                        )}
                                        {aircraftData.map((airCraft, index) => {
                                            return (
                                                <React.Fragment key={airCraft.tailNumber}>
                                                    <AircraftCard
                                                        key={airCraft.tailNumber}
                                                        id={airCraft.id}
                                                        legId={legId}
                                                        recommended={airCraft.recommended}
                                                        category={airCraft.category}
                                                        aircraftName={airCraft.aircraftName}
                                                        photoData={airCraft.photoData}
                                                        seats={airCraft.seats}
                                                        departureTime={airCraft.departureTime}
                                                        arrivalTime={airCraft.arrivalTime}
                                                        headRoom={airCraft.headRoom}
                                                        refurbishedYear={airCraft.refurbishedYear}
                                                        cabinWidth={airCraft.cabinWidth}
                                                        cateringOption={airCraft.cateringOption}
                                                        luggage={airCraft.luggage}
                                                        toilet={airCraft.toilet}
                                                        ownerApproval={airCraft.ownerApproval}
                                                        wifiOption={airCraft.wifiOption}
                                                        co2={airCraft.co2}
                                                        yom={airCraft.yom}
                                                        price={airCraft.price}
                                                        currency={airCraft.preferredCurrency}
                                                        operatorCurrency={airCraft.operatorCurrency}
                                                        expandedId={expanded}
                                                        expandedHandler={() =>
                                                            expandedHandler(airCraft.tailNumber, index)
                                                        }
                                                        dimAircraft={
                                                            expanded.size > 0 && !expanded.has(airCraft.tailNumber)
                                                        }
                                                        exitAircraft={exitAircraft}
                                                        enterAircraft={enterAircraft}
                                                        sharedPage={sharedPage}
                                                        sharerName={sharerName}
                                                        approvalRequested={approvalRequested}
                                                        thumbsStatus={thumbs.includes(airCraft.tailNumber)}
                                                        updateThumbsStatus={(status) =>
                                                            thumbsHandler(status, airCraft.tailNumber)
                                                        }
                                                        tailNumber={airCraft.tailNumber}
                                                        showCheckbox={showCompareBar}
                                                        compareSelectionHandler={(value) =>
                                                            compareSelectionHandler(
                                                                value,
                                                                `${airCraft.id}___${airCraft.tailNumber}`
                                                            )
                                                        }
                                                        compareSelected={compareSelection.includes(
                                                            `${airCraft.id}___${airCraft.tailNumber}`
                                                        )}
                                                        showMovida={searchResultsHeader?.showMovida}
                                                        aircraftData={airCraft}
                                                        tripInfo={searchResultsHeader}
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <Compare
                                aircraftData={aircraftData}
                                compareSelection={compareSelection}
                                legId={legId}
                                clearAllHandler={compareClearAllHandler}
                                sharedPage={sharedPage}
                                backLink={backLink}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default Quote;
